import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Table from 'react-bootstrap/Table'

import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'

import { getClientReport } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { formatCurrency, formatNumber } from '../../utilities/helpers'

import { ReportRefreshButton } from '../ReportRefreshButton'

import './clientReportWrapper.scss'

const CADENCE_OPTIONS = ['Monthly', 'Quarterly', 'Yearly']
const CHART_COLORS = [
  '#3d72bb',
  '#33a97f',
  '#8274df',
  '#e5923f',
  '#cf5d97',
  '#93989d',
]

const compareDelta = (current, previous) => {
  const currentValue = Number(current || 0)
  const previousValue = Number(previous || 0)
  if (!previousValue) return null
  return ((currentValue - previousValue) / Math.abs(previousValue)) * 100
}

const formatDeltaPct = (delta) => {
  if (delta == null) return '—'
  const sign = delta >= 0 ? '+' : ''
  return `${sign}${delta.toFixed(0)}%`
}

const formatCurrencyCompact = (value) => {
  const amount = Number(value || 0)
  if (!amount) return '$0'
  return `$${amount.toLocaleString(undefined, {
    notation: 'compact',
    maximumFractionDigits: 0,
  })}`
}

const formatSignedCurrencyCompact = (value) => {
  const amount = Number(value || 0)
  if (!amount) return '$0'
  return `${amount > 0 ? '+' : '-'}${formatCurrencyCompact(Math.abs(amount))}`
}

const buildLinePath = (values, width = 100, height = 100, padding = 8) => {
  if (!values?.length) return ''
  const max = Math.max(...values, 1)
  const min = 0
  const range = Math.max(max - min, 1)
  const points = values.map((value, idx) => {
    const x =
      padding + (idx * (width - padding * 2)) / Math.max(values.length - 1, 1)
    const y =
      height -
      padding -
      ((Number(value || 0) - min) / range) * (height - padding * 2)
    return [x, y]
  })

  return points
    .map(([x, y], idx) => `${idx === 0 ? 'M' : 'L'} ${x},${y}`)
    .join(' ')
}

function KpiCard({ title, value, delta, deltaSuffix, deltaType = 'percent' }) {
  const positive = delta == null ? null : delta >= 0
  const deltaLabel =
    delta == null
      ? '—'
      : deltaType === 'count'
        ? `${delta > 0 ? '+' : ''}${Math.round(delta)}`
        : formatDeltaPct(delta)

  return (
    <Card body className="client-report-kpi-card">
      <p className="client-report-kpi-title">{title}</p>
      <h3>{value}</h3>
      <p
        className={`client-report-kpi-delta ${
          positive == null
            ? 'is-neutral'
            : positive
              ? 'is-positive'
              : 'is-negative'
        }`}
      >
        {delta == null ? '—' : `${positive ? '↑' : '↓'} ${deltaLabel}`}{' '}
        {deltaSuffix}
      </p>
    </Card>
  )
}

function RevenueByClientCard({ clientRevenueRows }) {
  if (!clientRevenueRows.length) {
    return (
      <Card body className="client-report-panel">
        <h5 className="mb-3">Revenue by client</h5>
        <EmptyContainer style="center lg report">
          <p>No data for this timeframe.</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="client-report-panel">
      <h5 className="mb-3">Revenue by client</h5>
      <div className="client-report-category-list">
        {clientRevenueRows.map((row) => (
          <div key={row.label} className="client-report-category-row">
            <span className="client-report-category-name">{row.label}</span>
            <div className="client-report-category-bar-track">
              <div
                className="client-report-category-bar-fill"
                style={{
                  width: `${row.percent}%`,
                  backgroundColor: row.color,
                }}
              >
                <span>{row.percent}%</span>
              </div>
            </div>
            <strong>{formatCurrencyCompact(row.value)}</strong>
          </div>
        ))}
      </div>
    </Card>
  )
}

function EarningsBySalesChannelCard({ earningsRows, totalEarnings }) {
  if (!earningsRows.length || totalEarnings <= 0) {
    return (
      <Card body className="client-report-panel">
        <h5 className="mb-3">Earnings by sales channel</h5>
        <EmptyContainer style="center lg report">
          <p>No earnings data for this timeframe.</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="client-report-panel">
      <h5 className="mb-1">Earnings by sales channel</h5>
      <p className="client-report-subtitle mb-3">
        Total earnings: {formatCurrencyCompact(totalEarnings)}
      </p>
      <div className="client-report-ticket-legend client-report-earnings-legend mb-2">
        {earningsRows.map((row) => (
          <span
            key={row.label}
            className="client-report-earnings-legend-item"
            style={{
              '--legend-color': row.color,
            }}
          >
            {row.label}
          </span>
        ))}
      </div>
      <div className="client-report-category-bar-track client-report-earnings-stack-track mb-2">
        <div className="client-report-earnings-stack">
          {earningsRows.map((row) => (
            <span
              key={row.label}
              className="client-report-earnings-segment"
              style={{
                width: `${row.percent}%`,
                backgroundColor: row.color,
              }}
              title={`${row.label}: ${formatCurrencyCompact(row.value)} (${row.percent}%)`}
            >
              {row.percent >= 12 ? `${row.percent}%` : ''}
            </span>
          ))}
        </div>
      </div>
      <div className="client-report-category-list">
        {earningsRows.map((row) => (
          <div key={row.label} className="client-report-category-row">
            <span className="client-report-category-name">{row.label}</span>
            <span className="client-report-earnings-row-meta">
              {row.percent}%
            </span>
            <strong>{formatCurrencyCompact(row.value)}</strong>
          </div>
        ))}
      </div>
    </Card>
  )
}

function TicketDistributionCard({
  title,
  ticketDistributionRows,
  totalTicketsIssued,
  emptyMessage = 'No ticket data for this timeframe.',
}) {
  return (
    <Card body className="client-report-panel">
      <h5 className="mb-1">{title}</h5>
      <p className="client-report-subtitle mb-3">
        Total tickets issued: {formatNumber(totalTicketsIssued)}
      </p>
      {ticketDistributionRows.length ? (
        <div className="client-report-ticket-list">
          <div className="client-report-ticket-legend">
            <span className="is-sold">Sold</span>
            <span className="is-comp">Comp</span>
          </div>
          {ticketDistributionRows.map((row) => (
            <div key={row.label} className="client-report-ticket-row">
              <div className="client-report-ticket-labels">
                <span className="client-report-ticket-name">{row.label}</span>
                <span className="client-report-ticket-meta">
                  {formatNumber(row.soldTickets)} sold /{' '}
                  {formatNumber(row.compTickets)} comp
                </span>
              </div>
              <div className="client-report-category-bar-track">
                <div
                  className="client-report-ticket-stack"
                  style={{
                    width: `${row.percent}%`,
                  }}
                >
                  <span
                    className="client-report-ticket-segment is-sold"
                    style={{ width: `${row.soldPercent}%` }}
                  />
                  <span
                    className="client-report-ticket-segment is-comp"
                    style={{ width: `${row.compPercent}%` }}
                  />
                  <span className="client-report-ticket-stack-label">
                    {row.percent}%
                  </span>
                </div>
              </div>
              <strong>{formatNumber(row.totalTickets)}</strong>
            </div>
          ))}
        </div>
      ) : (
        <EmptyContainer style="center lg report">
          <p>{emptyMessage}</p>
        </EmptyContainer>
      )}
    </Card>
  )
}

function TicketDistributionByClientCard({
  ticketDistributionRows,
  totalTicketsIssued,
}) {
  return (
    <TicketDistributionCard
      title="Ticket Distribution by client"
      ticketDistributionRows={ticketDistributionRows}
      totalTicketsIssued={totalTicketsIssued}
    />
  )
}

function TicketDistributionBySalesChannelCard({
  ticketDistributionRows,
  totalTicketsIssued,
}) {
  return (
    <TicketDistributionCard
      title="Ticket Distribution by sales channel"
      ticketDistributionRows={ticketDistributionRows}
      totalTicketsIssued={totalTicketsIssued}
    />
  )
}

function ProcessingFeeDifferenceByClientCard({
  differenceRows,
  totalDifference,
}) {
  if (!differenceRows.length) {
    return (
      <Card body className="client-report-panel">
        <h5 className="mb-3">Estimated vs processing fee by client</h5>
        <EmptyContainer style="center lg report">
          <p>No processing fee difference data for this timeframe.</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="client-report-panel">
      <h5 className="mb-1">Estimated vs processing fee by client</h5>
      <p className="client-report-subtitle mb-3">
        Net difference:{' '}
        <span
          className={
            totalDifference >= 0
              ? 'client-report-positive'
              : 'client-report-negative'
          }
        >
          {formatSignedCurrencyCompact(totalDifference)}
        </span>{' '}
        (estimated processing fee minus processing fee)
      </p>
      <div className="client-report-ticket-list">
        {differenceRows.map((row) => (
          <div key={row.label} className="client-report-ticket-row">
            <div className="client-report-ticket-labels">
              <span className="client-report-ticket-name">{row.label}</span>
              <span className="client-report-ticket-meta">
                {formatCurrencyCompact(row.estimatedProcessingFees)} estimated /{' '}
                {formatCurrencyCompact(row.processingFees)} processing
              </span>
            </div>
            <div className="client-report-category-bar-track">
              <div
                className={`client-report-difference-fill ${
                  row.difference >= 0 ? 'is-positive' : 'is-negative'
                }`}
                style={{ width: `${row.percent}%` }}
              />
            </div>
            <strong
              className={
                row.difference >= 0
                  ? 'client-report-positive'
                  : 'client-report-negative'
              }
            >
              {formatSignedCurrencyCompact(row.difference)}
            </strong>
          </div>
        ))}
      </div>
    </Card>
  )
}

function ClientTrendChart({
  labels,
  trendSeries,
  title = 'Trend (last 6 months)',
}) {
  if (!labels.length || !trendSeries.length) {
    return (
      <Card body className="client-report-panel">
        <h5 className="mb-3">{title}</h5>
        <EmptyContainer style="center lg report">
          <p>No data for this timeframe.</p>
        </EmptyContainer>
      </Card>
    )
  }

  const allValues = trendSeries.flatMap((series) => series.values)
  const axisMax = Math.max(...allValues, 1)
  const ticks = [1, 0.8, 0.6, 0.4, 0.2, 0]

  return (
    <Card body className="client-report-panel">
      <h5 className="mb-2">{title}</h5>
      <div className="client-report-line-chart is-trend">
        <div className="client-report-line-axis-y">
          {ticks.map((ratio) => (
            <span key={ratio}>{formatCurrencyCompact(axisMax * ratio)}</span>
          ))}
        </div>
        <div className="client-report-line-svg-wrap">
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {ticks.map((ratio) => {
              const y = 8 + (1 - ratio) * 84
              return (
                <line
                  key={ratio}
                  x1="8"
                  y1={y}
                  x2="92"
                  y2={y}
                  className="client-report-grid-line"
                />
              )
            })}
            {trendSeries.map((series, index) => (
              <path
                key={series.label}
                d={buildLinePath(series.values)}
                className={`line ${index > 0 ? 'line-dashed' : ''}`}
                style={{ stroke: series.color }}
              />
            ))}
          </svg>
          <div className="client-report-line-axis-x">
            {labels.map((label, index) => (
              <span
                key={`${label}-${index}`}
                style={{
                  left: `${8 + index * (84 / Math.max(labels.length - 1, 1))}%`,
                }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="client-report-legend">
        {trendSeries.map((series) => (
          <span key={series.label}>
            <i className="dot" style={{ backgroundColor: series.color }} />
            {series.label}
          </span>
        ))}
      </div>
    </Card>
  )
}

export default function ClientReportWrapper() {
  const currentYear = moment().year()
  const [cadence, setCadence] = useState('Monthly')
  const [year, setYear] = useState(String(currentYear))
  const [isLoading, setIsLoading] = useState(false)
  const { refreshToken, refresh, completeRefresh } = useReportRefresh(isLoading)
  const [loadError, setLoadError] = useState('')
  const [reportData, setReportData] = useState(null)
  const [previousData, setPreviousData] = useState(null)

  const getDateRangeForCadence = (selectedCadence, selectedYear) => {
    const numericYear = Number(selectedYear)
    const now = moment()
    const periodEnd =
      numericYear === now.year()
        ? now.clone().endOf('day')
        : moment(`${numericYear}-12-31`).endOf('day')

    let periodStart
    switch (selectedCadence) {
      case 'Monthly':
        periodStart = periodEnd.clone().startOf('month')
        break
      case 'Quarterly':
        periodStart = periodEnd.clone().subtract(2, 'months').startOf('month')
        break
      case 'Yearly':
      default:
        periodStart = periodEnd.clone().startOf('year')
        break
    }

    return {
      from: periodStart.format('YYYY-MM-DD'),
      to: periodEnd.format('YYYY-MM-DD'),
      fromMoment: periodStart,
      toMoment: periodEnd,
    }
  }

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (!year) return
      setIsLoading(true)
      setLoadError('')
      try {
        const { from, to, fromMoment, toMoment } = getDateRangeForCadence(
          cadence,
          year
        )
        const periodDays =
          toMoment
            .clone()
            .startOf('day')
            .diff(fromMoment.clone().startOf('day'), 'days') + 1
        const prevToMoment = fromMoment.clone().subtract(1, 'day').endOf('day')
        const prevFromMoment = prevToMoment
          .clone()
          .subtract(periodDays - 1, 'days')
          .startOf('day')
        const prevFrom = prevFromMoment.format('YYYY-MM-DD')
        const prevTo = prevToMoment.format('YYYY-MM-DD')

        const [currentRes, previousRes] = await Promise.all([
          getClientReport(from, to),
          getClientReport(prevFrom, prevTo),
        ])

        if (cancelled) return
        setReportData(currentRes?.data ?? null)
        setPreviousData(previousRes?.data ?? null)
      } catch (error) {
        if (cancelled) return
        console.error('Failed to load client report data', error)
        setReportData(null)
        setPreviousData(null)
        setLoadError('Unable to load client report data for the selected year.')
      } finally {
        if (!cancelled) {
          setIsLoading(false)
          completeRefresh()
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [cadence, year, refreshToken])

  const yearOptions = useMemo(
    () => [currentYear, currentYear - 1, currentYear - 2],
    [currentYear]
  )

  const summary = reportData?.summary ?? {}
  const previousSummary = previousData?.summary ?? {}

  const metrics = useMemo(() => {
    const totalRevenue = Number(summary.totalServiceFees || 0)
    const previousTotalRevenue = Number(previousSummary.totalServiceFees || 0)

    const avgFeePerTicket = Number(summary.avgFeePerTicket || 0)
    const previousAvgFeePerTicket = Number(previousSummary.avgFeePerTicket || 0)

    const activeClients = Number(summary.activeClients || 0)
    const previousActiveClients = Number(previousSummary.activeClients || 0)

    const ticketsProcessed = Number(summary.ticketsProcessed || 0)
    const previousTicketsProcessed = Number(
      previousSummary.ticketsProcessed || 0
    )

    return {
      totalRevenue,
      totalRevenueDelta: compareDelta(totalRevenue, previousTotalRevenue),
      avgFeePerTicket,
      avgFeePerTicketDelta: compareDelta(
        avgFeePerTicket,
        previousAvgFeePerTicket
      ),
      activeClients,
      activeClientsDelta: activeClients - previousActiveClients,
      ticketsProcessed,
      ticketsProcessedDelta: compareDelta(
        ticketsProcessed,
        previousTicketsProcessed
      ),
    }
  }, [previousSummary, summary])

  const clientRevenueRows = useMemo(() => {
    const rows = (reportData?.revenueByClient ?? [])
      .slice(0, 6)
      .map((row, idx) => ({
        label: row.organizationName || 'Unknown client',
        value: Number(row.serviceFees || 0),
        color: CHART_COLORS[idx % CHART_COLORS.length],
      }))
      .filter((row) => row.value > 0)
    const total = rows.reduce((sum, row) => sum + row.value, 0)
    return rows.map((row) => ({
      ...row,
      percent: total ? Math.max(1, Math.round((row.value / total) * 100)) : 0,
    }))
  }, [reportData?.revenueByClient])

  const trendData = useMemo(() => {
    const labels = reportData?.trend?.months ?? []
    const clients = (reportData?.trend?.clients ?? []).slice(0, 3)

    return {
      labels,
      series: clients.map((client, idx) => ({
        label: client.organizationName || `Client ${idx + 1}`,
        color: CHART_COLORS[idx % CHART_COLORS.length],
        values: (client.monthlyServiceFees ?? []).map((value) =>
          Number(value || 0)
        ),
      })),
    }
  }, [reportData?.trend?.clients, reportData?.trend?.months])

  const previousClientRevenueByOrg = useMemo(() => {
    return (previousData?.revenueByClient ?? []).reduce((acc, row) => {
      acc[row.organizationUUID] = Number(row.serviceFees || 0)
      return acc
    }, {})
  }, [previousData?.revenueByClient])

  const clientRows = useMemo(
    () =>
      (reportData?.revenueByClient ?? []).slice(0, 8).map((row) => {
        const serviceFees = Number(row.serviceFees || 0)
        const tickets = Number(row.ticketsProcessed || 0)
        const ticketsWithServiceFee = Number(row.ticketsWithServiceFee || 0)
        const previousRevenue = Number(
          previousClientRevenueByOrg[row.organizationUUID] || 0
        )

        return {
          name: row.organizationName || 'Unknown client',
          tickets,
          feeRate: ticketsWithServiceFee
            ? serviceFees / ticketsWithServiceFee
            : 0,
          clientGross: Number(row.grossRevenue || 0),
          revenue: serviceFees,
          mom: compareDelta(serviceFees, previousRevenue),
          status: row.isActive ? 'Active' : 'Trial',
        }
      }),
    [previousClientRevenueByOrg, reportData?.revenueByClient]
  )

  const ticketDistributionRows = useMemo(() => {
    const rows = (reportData?.revenueByClient ?? [])
      .slice(0, 6)
      .map((row) => {
        const totalTickets = Number(row.ticketsProcessed || 0)
        const soldTickets = Math.min(
          totalTickets,
          Number(row.ticketsWithServiceFee || 0)
        )
        const rawCompTickets = Math.max(0, totalTickets - soldTickets)
        const reservedCompTickets = Math.max(
          0,
          Number(row.reservedCompTickets || 0)
        )
        const compTickets =
          rawCompTickets - Math.min(rawCompTickets, reservedCompTickets)
        const totalTicketsForDistribution = soldTickets + compTickets
        return {
          label: row.organizationName || 'Unknown client',
          totalTickets: totalTicketsForDistribution,
          soldTickets,
          compTickets,
        }
      })
      .filter((row) => row.totalTickets > 0)
    const total = rows.reduce((sum, row) => sum + row.totalTickets, 0)
    return rows.map((row) => ({
      ...row,
      soldPercent: row.totalTickets
        ? (row.soldTickets / row.totalTickets) * 100
        : 0,
      compPercent: row.totalTickets
        ? (row.compTickets / row.totalTickets) * 100
        : 0,
      percent: total
        ? Math.max(1, Math.round((row.totalTickets / total) * 100))
        : 0,
    }))
  }, [reportData?.revenueByClient])

  const totalTicketsIssued = useMemo(
    () =>
      ticketDistributionRows.reduce((sum, row) => sum + row.totalTickets, 0),
    [ticketDistributionRows]
  )

  const ticketDistributionBySalesChannelRows = useMemo(() => {
    const rows = (reportData?.ticketDistributionByChannel ?? [])
      .map((row) => {
        const totalTickets = Number(row.ticketsProcessed || 0)
        const soldTickets = Math.min(
          totalTickets,
          Number(row.ticketsWithServiceFee || 0)
        )
        const rawCompTickets = Math.max(0, totalTickets - soldTickets)
        const reservedCompTickets = Math.max(
          0,
          Number(row.reservedCompTickets || 0)
        )
        const compTickets =
          rawCompTickets - Math.min(rawCompTickets, reservedCompTickets)
        const totalTicketsForDistribution = soldTickets + compTickets
        return {
          label: row.label || 'Unknown channel',
          totalTickets: totalTicketsForDistribution,
          soldTickets,
          compTickets,
        }
      })
      .filter((row) => row.totalTickets > 0)
    const total = rows.reduce((sum, row) => sum + row.totalTickets, 0)
    return rows.map((row) => ({
      ...row,
      soldPercent: row.totalTickets
        ? (row.soldTickets / row.totalTickets) * 100
        : 0,
      compPercent: row.totalTickets
        ? (row.compTickets / row.totalTickets) * 100
        : 0,
      percent: total
        ? Math.max(1, Math.round((row.totalTickets / total) * 100))
        : 0,
    }))
  }, [reportData?.ticketDistributionByChannel])

  const totalTicketsBySalesChannel = useMemo(
    () =>
      ticketDistributionBySalesChannelRows.reduce(
        (sum, row) => sum + row.totalTickets,
        0
      ),
    [ticketDistributionBySalesChannelRows]
  )

  const earningsBySalesChannelRows = useMemo(() => {
    const colorByChannel = {
      ticket_assignment: '#3d72bb',
      box_office: '#33a97f',
      website: '#e5923f',
    }
    const rows = (reportData?.earningsBySalesChannel ?? []).map((row, idx) => ({
      label: row.label || 'Unknown channel',
      channel: row.channel || '',
      value: Number(row.earnings || 0),
      color:
        colorByChannel[row.channel] || CHART_COLORS[idx % CHART_COLORS.length],
    }))
    const total = rows.reduce((sum, row) => sum + row.value, 0)
    return rows.map((row) => ({
      ...row,
      percent: total ? Number(((row.value / total) * 100).toFixed(1)) : 0,
    }))
  }, [reportData?.earningsBySalesChannel])

  const totalEarningsBySalesChannel = useMemo(
    () => earningsBySalesChannelRows.reduce((sum, row) => sum + row.value, 0),
    [earningsBySalesChannelRows]
  )

  const processingFeeDifferenceRows = useMemo(() => {
    const rows = (reportData?.revenueByClient ?? [])
      .map((row) => {
        const processingFees = Number(row.processingFees || 0)
        const estimatedProcessingFees = Number(row.estimatedProcessingFees || 0)
        const difference = estimatedProcessingFees - processingFees
        return {
          label: row.organizationName || 'Unknown client',
          processingFees,
          estimatedProcessingFees,
          difference,
          magnitude: Math.abs(difference),
        }
      })
      .filter((row) => row.magnitude > 0)
      .sort((a, b) => b.magnitude - a.magnitude)
      .slice(0, 6)
    const maxMagnitude = rows.reduce(
      (max, row) => Math.max(max, row.magnitude),
      0
    )
    return rows.map((row) => ({
      ...row,
      percent: maxMagnitude
        ? Math.max(6, Math.round((row.magnitude / maxMagnitude) * 100))
        : 0,
    }))
  }, [reportData?.revenueByClient])

  const totalProcessingFeeDifference = useMemo(
    () =>
      processingFeeDifferenceRows.reduce((sum, row) => sum + row.difference, 0),
    [processingFeeDifferenceRows]
  )

  return (
    <section className="spacer-sm" id="client-report-wrapper">
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Client Report</h1>
        </div>
      </header>

      <div className="client-report-filters mb-3">
        <Form.Select
          value={cadence}
          onChange={(event) => setCadence(event.target.value)}
          aria-label="Cadence"
        >
          {CADENCE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          value={year}
          onChange={(event) => setYear(event.target.value)}
          aria-label="Year"
        >
          {yearOptions.map((optionYear) => (
            <option key={optionYear} value={String(optionYear)}>
              {optionYear}
            </option>
          ))}
        </Form.Select>
        <ReportRefreshButton
          onRefresh={refresh}
          isRefreshing={isLoading}
          disabled={isLoading || !year}
        />
      </div>

      {isLoading ? (
        <LoadingContainer />
      ) : loadError ? (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>{loadError}</p>
          </EmptyContainer>
        </Card>
      ) : !reportData ? (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>No client report data found.</p>
          </EmptyContainer>
        </Card>
      ) : (
        <>
          <p className="client-report-section-title">Overview</p>
          <Row className="g-3 mb-4">
            <Col md={6} xl={3}>
              <KpiCard
                title="Total revenue"
                value={formatCurrencyCompact(metrics.totalRevenue)}
                delta={metrics.totalRevenueDelta}
                deltaSuffix="vs last period"
              />
            </Col>
            <Col md={6} xl={3}>
              <KpiCard
                title="Avg fee per ticket"
                value={formatCurrency(metrics.avgFeePerTicket)}
                delta={metrics.avgFeePerTicketDelta}
                deltaSuffix="vs last period"
              />
            </Col>
            <Col md={6} xl={3}>
              <KpiCard
                title="Active clients"
                value={formatNumber(metrics.activeClients)}
                delta={metrics.activeClientsDelta}
                deltaSuffix="new this period"
                deltaType="count"
              />
            </Col>
            <Col md={6} xl={3}>
              <KpiCard
                title="Tickets processed"
                value={formatNumber(metrics.ticketsProcessed)}
                delta={metrics.ticketsProcessedDelta}
                deltaSuffix="vs last period"
              />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xl={7}>
              <RevenueByClientCard clientRevenueRows={clientRevenueRows} />
            </Col>
            <Col xl={5}>
              <ClientTrendChart
                labels={trendData.labels}
                trendSeries={trendData.series}
              />
            </Col>
          </Row>

          <div className="mb-3">
            <ProcessingFeeDifferenceByClientCard
              differenceRows={processingFeeDifferenceRows}
              totalDifference={totalProcessingFeeDifference}
            />
          </div>

          <p className="client-report-section-title">Client breakdown</p>
          <Card body className="client-report-panel client-report-table-wrap">
            <Table responsive borderless hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Tickets</th>
                  <th>Fee rate</th>
                  <th>Client Gross</th>
                  <th>Service Fee</th>
                  <th>MoM</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {clientRows.length ? (
                  clientRows.map((row) => (
                    <tr key={row.name}>
                      <td>{row.name}</td>
                      <td>{formatNumber(row.tickets)}</td>
                      <td>{formatCurrency(row.feeRate)}</td>
                      <td>{formatCurrency(row.clientGross)}</td>
                      <td>{formatCurrency(row.revenue)}</td>
                      <td
                        className={row.mom < 0 ? 'text-danger' : 'text-success'}
                      >
                        {row.mom == null ? '—' : formatDeltaPct(row.mom)}
                      </td>
                      <td>
                        <span
                          className={`client-report-status-badge ${
                            row.status === 'Active' ? 'is-active' : 'is-trial'
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No client rows for this range.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card>

          <div className="mt-3">
            <EarningsBySalesChannelCard
              earningsRows={earningsBySalesChannelRows}
              totalEarnings={totalEarningsBySalesChannel}
            />
          </div>

          <div className="mt-3">
            <TicketDistributionByClientCard
              ticketDistributionRows={ticketDistributionRows}
              totalTicketsIssued={totalTicketsIssued}
            />
          </div>

          <div className="mt-3">
            <TicketDistributionBySalesChannelCard
              ticketDistributionRows={ticketDistributionBySalesChannelRows}
              totalTicketsIssued={totalTicketsBySalesChannel}
            />
          </div>
        </>
      )}
    </section>
  )
}
